import { createRpcTransportPair } from "./kernel-connector.js";
export class KernelHostClosedError extends Error {
    constructor() {
        super("Kernel host is closed");
        this.name = "KernelHostClosedError";
    }
}
export class LeaderTabKernelHost {
    #channel;
    #channelName;
    #connections = new Map();
    #connectionLeaseMs;
    #leaderProbeIntervalMs;
    #leaderWaiters = new Set();
    #locks;
    #localStorage;
    #maintenanceIntervalMs;
    #now;
    #runtimeFactory;
    #scheduleMaintenance;
    #tabId;
    #trackedTransports = new Set();
    #closed = false;
    #electionLoop;
    #electionReady;
    #heldLockRelease;
    #knownLeaderId;
    #leaderProbe;
    #leaderWait;
    #maintenanceTask;
    #runtime;
    role = "follower";
    constructor(options) {
        this.#channelName = options.channelName;
        this.#channel = options.createChannel(options.channelName);
        this.#locks = options.locks;
        this.#connectionLeaseMs = options.connectionLeaseMs ?? 15_000;
        this.#leaderProbeIntervalMs = options.leaderProbeIntervalMs ?? 50;
        this.#localStorage = options.localStorage;
        this.#maintenanceIntervalMs = options.maintenanceIntervalMs ?? 5_000;
        this.#now = options.now ?? (() => Date.now());
        this.#runtimeFactory = options.runtimeFactory;
        this.#scheduleMaintenance = options.scheduleMaintenance ?? defaultMaintenanceScheduler;
        this.#tabId = options.tabId;
        this.#channel.addEventListener("message", (event) => {
            this.#handleMessage(event.data);
        });
    }
    connect() {
        const transport = this.role === "leader"
            ? this.#connectLocal()
            : new BroadcastRpcTransport({
                closeConnection: (connectionId, fromTabId) => {
                    if (this.role === "leader")
                        this.#closeLeaderConnection(connectionId, fromTabId);
                    else
                        this.#channel.postMessage({ connectionId, fromTabId, type: "rpc.close" });
                },
                ensureLeader: async () => await this.#awaitLeader(),
                onClose: (current) => {
                    this.#trackedTransports.delete(current);
                },
                sendMessage: (connectionId, fromTabId, message) => {
                    if (this.role === "leader") {
                        this.#leaderConnection(fromTabId, connectionId)?.transport.send(message);
                    }
                    else {
                        this.#channel.postMessage({ connectionId, fromTabId, message, type: "rpc.message" });
                    }
                },
                tabId: this.#tabId,
            });
        this.#trackTransport(transport);
        return transport;
    }
    async close() {
        if (this.#closed)
            return;
        this.#closed = true;
        this.#maintenanceTask?.cancel();
        this.#maintenanceTask = undefined;
        this.#rejectLeaderWaiters(new KernelHostClosedError());
        for (const transport of [...this.#trackedTransports])
            transport.close();
        this.#trackedTransports.clear();
        const wasLeader = this.role === "leader";
        let shutdownError;
        if (wasLeader) {
            for (const connection of [...this.#connections.values()])
                connection.transport.close();
            this.#connections.clear();
            try {
                await this.#runtime?.shutdown();
            }
            catch (error) {
                shutdownError = error;
            }
            finally {
                this.#runtime = undefined;
                this.#heldLockRelease?.();
                this.#heldLockRelease = undefined;
                await this.#electionLoop;
                this.#channel.postMessage({ leaderId: this.#tabId, type: "leader.closed" });
            }
        }
        this.role = "follower";
        this.#knownLeaderId = undefined;
        this.#channel.close();
        if (shutdownError !== undefined)
            throw shutdownError;
    }
    async wake() {
        if (this.#closed)
            return;
        await this.start();
        if (this.role === "leader") {
            await this.#runtime?.wake?.();
            return;
        }
        this.#channel.postMessage({ type: "sync.wake" });
    }
    async start() {
        if (this.#closed)
            return;
        if (this.role === "leader")
            return;
        if (this.#electionLoop !== undefined) {
            await this.#electionReady;
            return;
        }
        let readyResolve;
        let readyReject;
        const ready = new Promise((resolve, reject) => {
            readyResolve = resolve;
            readyReject = reject;
        });
        this.#electionReady = ready;
        this.#electionLoop = this.#locks.request(`leader:${this.#channelName}`, { ifAvailable: true, mode: "exclusive" }, async (lock) => {
            if (lock === null) {
                this.role = "follower";
                readyResolve?.();
                return;
            }
            if (this.#closed) {
                readyResolve?.();
                return;
            }
            this.role = "leader";
            this.#maintenanceTask?.cancel();
            this.#maintenanceTask = undefined;
            this.#knownLeaderId = this.#tabId;
            this.#runtime = this.#runtimeFactory();
            this.#resolveLeaderWaiters(this.#tabId);
            this.#persistHeartbeat();
            this.#reconnectTrackedTransports();
            this.#channel.postMessage({ leaderId: this.#tabId, type: "leader.announce" });
            readyResolve?.();
            await new Promise((resolve) => {
                this.#heldLockRelease = resolve;
            });
        }).catch((error) => {
            this.role = "follower";
            this.#knownLeaderId = undefined;
            this.#runtime = undefined;
            readyReject?.(error);
        }).finally(() => {
            this.#electionLoop = undefined;
            this.#electionReady = undefined;
        });
        await ready;
        this.#scheduleNextMaintenance();
    }
    /**
     * Resolves only once a leader has actually *taken office* — either this tab won the
     * Web Lock, or a peer announced itself on the control channel.
     *
     * Issue #1690. Election runs through `locks.request(..., { ifAvailable: true })`, which
     * is zero-wait: it reports the lock unavailable from the instant it is granted to the
     * winner, which is strictly BEFORE the winner's callback body runs and assigns
     * `role = "leader"`. Inside that window every tab — the winner included — still reads
     * as a follower, so a follower that lost the election and immediately posted
     * `rpc.message` had it discarded by `#handleMessage`'s leader-only guard in every tab
     * that received it. `BroadcastRpcTransport.send` is fire-and-forget (no ack, no retry;
     * `#replayMessages` only ever replays subscriptions and stage sessions, never commands
     * or queries) and `MessagePortKernelRpcClient` puts no timeout above it, so the caller's
     * promise never settled — the shell sat on "Securing this browser" forever because its
     * opening `client-device.session.v1` had evaporated. Holding the send until leadership
     * is observable closes that window.
     *
     * The same wait covers the wider gap after `leader.closed`, where the outgoing leader's
     * lock release is still settling and every survivor's `ifAvailable` probe legitimately
     * returns null, so re-running the election is part of the loop rather than a one-shot.
     */
    async #awaitLeader() {
        if (this.#closed)
            throw new KernelHostClosedError();
        await this.start();
        if (this.role === "leader")
            return this.#tabId;
        if (this.#knownLeaderId !== undefined)
            return this.#knownLeaderId;
        // One shared promise for all waiters keeps queued sends in FIFO order: `.then`
        // callbacks on a single promise run in registration order, so RPC ordering across
        // the wait is identical to the unblocked path.
        this.#leaderWait ??= this.#watchForLeader();
        return await this.#leaderWait;
    }
    async #watchForLeader() {
        try {
            return await new Promise((resolve, reject) => {
                this.#leaderWaiters.add({ reject, resolve });
                // A leader elected before this tab attached its channel listener never re-announces
                // on its own, so ask directly rather than waiting for an announcement that already
                // happened.
                this.#channel.postMessage({ type: "leader.whois" });
                this.#scheduleLeaderProbe();
            });
        }
        finally {
            this.#leaderWait = undefined;
        }
    }
    #scheduleLeaderProbe() {
        if (this.#closed || this.#leaderProbe !== undefined || this.#leaderWaiters.size === 0)
            return;
        this.#leaderProbe = this.#scheduleMaintenance(this.#leaderProbeIntervalMs, () => {
            this.#leaderProbe = undefined;
            void this.#probeForLeader();
        });
    }
    async #probeForLeader() {
        if (this.#closed || this.#leaderWaiters.size === 0)
            return;
        this.#channel.postMessage({ type: "leader.whois" });
        try {
            await this.start();
        }
        catch (error) {
            // A failed election is terminal for the callers already queued behind it: surface it
            // so their transports close and report `connection_lost`, instead of leaving them
            // parked on a leader that will never arrive.
            this.#rejectLeaderWaiters(error);
            return;
        }
        if (this.role === "leader")
            this.#resolveLeaderWaiters(this.#tabId);
        else if (this.#knownLeaderId !== undefined)
            this.#resolveLeaderWaiters(this.#knownLeaderId);
        this.#scheduleLeaderProbe();
    }
    #resolveLeaderWaiters(leaderId) {
        if (this.#leaderWaiters.size === 0)
            return;
        const waiters = [...this.#leaderWaiters];
        this.#leaderWaiters.clear();
        this.#leaderProbe?.cancel();
        this.#leaderProbe = undefined;
        for (const waiter of waiters)
            waiter.resolve(leaderId);
    }
    #rejectLeaderWaiters(error) {
        if (this.#leaderWaiters.size === 0)
            return;
        const waiters = [...this.#leaderWaiters];
        this.#leaderWaiters.clear();
        this.#leaderProbe?.cancel();
        this.#leaderProbe = undefined;
        for (const waiter of waiters)
            waiter.reject(error);
    }
    #connectLocal() {
        if (this.#runtime === undefined)
            this.#runtime = this.#runtimeFactory();
        const [clientTransport, serverTransport] = createRpcTransportPair();
        const detach = this.#runtime.attach(serverTransport);
        const connectionId = `${this.#tabId}:local:${this.#connections.size + 1}`;
        this.#connections.set(connectionId, {
            lastSeenAt: this.#now(),
            ownerTabId: this.#tabId,
            transport: serverTransport,
        });
        serverTransport.subscribeClose(() => {
            detach();
            this.#connections.delete(connectionId);
        });
        return clientTransport;
    }
    #handleMessage(message) {
        if (this.#closed || !isControlMessage(message))
            return;
        switch (message.type) {
            case "leader.announce":
                if (this.role === "leader" && message.leaderId !== this.#tabId)
                    return;
                this.#resolveLeaderWaiters(message.leaderId);
                if (this.#knownLeaderId === message.leaderId)
                    return;
                this.#knownLeaderId = message.leaderId;
                this.#persistHeartbeat(message.leaderId);
                this.#reconnectTrackedTransports();
                return;
            case "follower.heartbeat":
                if (this.role === "leader")
                    this.#touchFollower(message.tabId);
                return;
            case "leader.closed":
                if (this.#knownLeaderId !== message.leaderId)
                    return;
                this.#knownLeaderId = undefined;
                this.#queueElection();
                return;
            case "leader.whois":
                // Announcing is idempotent for peers (`leader.announce` short-circuits on an
                // unchanged id), so replying unconditionally costs nothing and lets a tab that
                // attached late learn the sitting leader without waiting for a failover.
                if (this.role === "leader") {
                    this.#channel.postMessage({ leaderId: this.#tabId, type: "leader.announce" });
                }
                return;
            case "sync.wake":
                if (this.role === "leader")
                    void Promise.resolve(this.#runtime?.wake?.()).catch(() => undefined);
                return;
            case "rpc.message":
                if (this.role !== "leader")
                    return;
                this.#leaderConnection(message.fromTabId, message.connectionId)?.transport.send(message.message);
                return;
            case "rpc.reply":
                if (message.toTabId !== this.#tabId)
                    return;
                BroadcastRpcTransport.dispatch(message.connectionId, message.message);
                return;
            case "rpc.expired":
                if (message.toTabId === this.#tabId)
                    BroadcastRpcTransport.reconnectConnection(message.connectionId);
                return;
            case "rpc.close":
                if (this.role !== "leader")
                    return;
                this.#closeLeaderConnection(message.connectionId, message.fromTabId);
        }
    }
    #closeLeaderConnection(connectionId, ownerTabId) {
        const connection = this.#connections.get(connectionId);
        if (connection === undefined || (ownerTabId !== undefined && connection.ownerTabId !== ownerTabId))
            return;
        connection.transport.close();
        this.#connections.delete(connectionId);
    }
    #leaderConnection(fromTabId, connectionId) {
        const existing = this.#connections.get(connectionId);
        if (existing !== undefined) {
            if (existing.ownerTabId !== fromTabId)
                return undefined;
            existing.lastSeenAt = this.#now();
            return existing;
        }
        if (this.#runtime === undefined)
            this.#runtime = this.#runtimeFactory();
        const [leaderTransport, runtimeTransport] = createRpcTransportPair();
        const detach = this.#runtime.attach(runtimeTransport);
        leaderTransport.subscribe((message) => {
            if (fromTabId === this.#tabId) {
                BroadcastRpcTransport.dispatch(connectionId, message);
            }
            else {
                this.#channel.postMessage({
                    connectionId,
                    message,
                    toTabId: fromTabId,
                    type: "rpc.reply",
                });
            }
        });
        leaderTransport.subscribeClose(() => {
            detach();
            this.#connections.delete(connectionId);
        });
        const connection = { lastSeenAt: this.#now(), ownerTabId: fromTabId, transport: leaderTransport };
        this.#connections.set(connectionId, connection);
        return connection;
    }
    #persistHeartbeat(leaderId = this.#tabId) {
        try {
            this.#localStorage?.setItem("node-client:leader-heartbeat", JSON.stringify({
                leaderId,
                tabId: this.#tabId,
            }));
        }
        catch {
            // This heartbeat is diagnostic only and must never influence lock authority.
        }
    }
    #queueElection() {
        if (this.#closed || this.role === "leader")
            return;
        void this.start();
    }
    async #maintain() {
        if (this.#closed)
            return;
        if (this.role === "leader") {
            const staleBefore = this.#now() - this.#connectionLeaseMs;
            for (const [connectionId, connection] of [...this.#connections]) {
                if (connection.ownerTabId !== this.#tabId && connection.lastSeenAt <= staleBefore) {
                    this.#closeLeaderConnection(connectionId, connection.ownerTabId);
                    this.#channel.postMessage({
                        connectionId,
                        toTabId: connection.ownerTabId,
                        type: "rpc.expired",
                    });
                }
            }
        }
        else {
            this.#channel.postMessage({ tabId: this.#tabId, type: "follower.heartbeat" });
            await this.start();
        }
        this.#scheduleNextMaintenance();
    }
    #scheduleNextMaintenance() {
        if (this.#closed || this.#maintenanceTask !== undefined)
            return;
        this.#maintenanceTask = this.#scheduleMaintenance(this.#maintenanceIntervalMs, () => {
            this.#maintenanceTask = undefined;
            void this.#maintain();
        });
    }
    #touchFollower(tabId) {
        const now = this.#now();
        for (const connection of this.#connections.values()) {
            if (connection.ownerTabId === tabId)
                connection.lastSeenAt = now;
        }
    }
    #trackTransport(transport) {
        this.#trackedTransports.add(transport);
        transport.subscribeClose(() => {
            this.#trackedTransports.delete(transport);
        });
    }
    #reconnectTrackedTransports() {
        for (const transport of this.#trackedTransports) {
            if (transport instanceof BroadcastRpcTransport)
                transport.reconnect();
        }
    }
}
class BroadcastRpcTransport {
    static transports = new Map();
    #closeConnection;
    #connectionId;
    #ensureLeader;
    #messageListeners = new Set();
    #closeListeners = new Set();
    #internalSessionOpens = new Map();
    #onClose;
    #pendingSessionOpens = new Map();
    #replayMessages = new Map();
    #sendMessage;
    #sessions = new Map();
    #tabId;
    #nextReconnectRequestId = 1;
    #reconnectPromise = Promise.resolve();
    state = "open";
    constructor(options) {
        this.#closeConnection = options.closeConnection;
        this.#ensureLeader = options.ensureLeader;
        this.#onClose = options.onClose;
        this.#sendMessage = options.sendMessage;
        this.#tabId = options.tabId;
        this.#connectionId = `${options.tabId}:${Math.random().toString(36).slice(2)}`;
        BroadcastRpcTransport.transports.set(this.#connectionId, this);
    }
    static dispatch(connectionId, message) {
        const transport = BroadcastRpcTransport.transports.get(connectionId);
        if (transport !== undefined)
            transport.#receive(message);
    }
    static reconnectConnection(connectionId) {
        BroadcastRpcTransport.transports.get(connectionId)?.reconnect();
    }
    close() {
        if (this.state === "closed")
            return;
        this.state = "closed";
        this.#closeConnection(this.#connectionId, this.#tabId);
        BroadcastRpcTransport.transports.delete(this.#connectionId);
        for (const pending of this.#internalSessionOpens.values())
            pending.reject(new Error("RPC transport closed"));
        this.#internalSessionOpens.clear();
        this.#onClose(this);
        for (const listener of [...this.#closeListeners])
            listener();
    }
    send(message) {
        this.#trackProtocolState(message);
        void this.#ensureLeader().then(async () => {
            await this.#reconnectPromise;
            if (this.state === "open") {
                this.#sendProjected(message);
                this.#forgetClosedSession(message);
            }
        }).catch(() => {
            this.close();
        });
    }
    reconnect() {
        if (this.state !== "open")
            return;
        this.#reconnectPromise = this.#reconnectPromise
            .then(async () => await this.#performReconnect());
        void this.#reconnectPromise.catch(() => {
            this.close();
        });
    }
    subscribe(listener) {
        this.#messageListeners.add(listener);
        return () => {
            this.#messageListeners.delete(listener);
        };
    }
    subscribeClose(listener) {
        this.#closeListeners.add(listener);
        return () => {
            this.#closeListeners.delete(listener);
        };
    }
    async #performReconnect() {
        if (this.state !== "open")
            return;
        for (const [oldSessionId, session] of this.#sessions) {
            const requestId = `leader-reconnect:${this.#connectionId}:${this.#nextReconnectRequestId++}`;
            const nextSessionId = await new Promise((resolve, reject) => {
                this.#internalSessionOpens.set(requestId, { oldSessionId, reject, resolve });
                this.#sendRaw(JSON.stringify({
                    appName: session.appName,
                    id: requestId,
                    manifestSha256: session.manifestSha256,
                    type: "stage.open-session",
                }));
            });
            session.currentSessionId = nextSessionId;
        }
        for (const pending of this.#pendingSessionOpens.values())
            this.#sendProjected(pending.message);
        for (const message of this.#replayMessages.values())
            this.#sendProjected(message);
    }
    #projectMessage(message) {
        const value = parseRecord(message);
        if (value === undefined || typeof value.sessionId !== "string")
            return message;
        const oldSessionId = this.#sessionKey(value.sessionId);
        const session = oldSessionId === undefined ? undefined : this.#sessions.get(oldSessionId);
        if (session === undefined || session.currentSessionId === value.sessionId)
            return message;
        return JSON.stringify({ ...value, sessionId: session.currentSessionId });
    }
    #receive(message) {
        const value = parseRecord(message);
        const responseId = value?.id;
        if (typeof responseId === "string") {
            const internal = this.#internalSessionOpens.get(responseId);
            if (internal !== undefined) {
                this.#internalSessionOpens.delete(responseId);
                const sessionId = value === undefined ? undefined : responseSessionId(value);
                if (sessionId === undefined)
                    internal.reject(new Error("Stage session could not be restored"));
                else
                    internal.resolve(sessionId);
                return;
            }
            const pending = this.#pendingSessionOpens.get(responseId);
            if (pending !== undefined) {
                this.#pendingSessionOpens.delete(responseId);
                const sessionId = value === undefined ? undefined : responseSessionId(value);
                if (sessionId !== undefined) {
                    this.#sessions.set(sessionId, {
                        appName: pending.appName,
                        currentSessionId: sessionId,
                        manifestSha256: pending.manifestSha256,
                    });
                }
            }
        }
        for (const listener of [...this.#messageListeners])
            listener(message);
    }
    #sendProjected(message) {
        this.#sendRaw(this.#projectMessage(message));
    }
    #sendRaw(message) {
        this.#sendMessage(this.#connectionId, this.#tabId, message);
    }
    #sessionKey(sessionId) {
        if (this.#sessions.has(sessionId))
            return sessionId;
        for (const [oldSessionId, session] of this.#sessions) {
            if (session.currentSessionId === sessionId)
                return oldSessionId;
        }
        return undefined;
    }
    #forgetClosedSession(message) {
        const value = parseRecord(message);
        if (value?.type !== "stage.close-session" || typeof value.sessionId !== "string")
            return;
        const oldSessionId = this.#sessionKey(value.sessionId);
        if (oldSessionId === undefined)
            return;
        this.#sessions.delete(oldSessionId);
        for (const [key, replay] of [...this.#replayMessages]) {
            const replayValue = parseRecord(replay);
            if (replayValue?.sessionId === oldSessionId ||
                replayValue?.sessionId === value.sessionId) {
                this.#replayMessages.delete(key);
            }
        }
    }
    #trackProtocolState(message) {
        const value = parseRecord(message);
        if (value === undefined || typeof value.type !== "string")
            return;
        if (value.type === "stage.open-session" &&
            typeof value.id === "string" &&
            typeof value.appName === "string" &&
            typeof value.manifestSha256 === "string") {
            this.#pendingSessionOpens.set(value.id, {
                appName: value.appName,
                manifestSha256: value.manifestSha256,
                message,
            });
            return;
        }
        if (value.type === "stage.close-session")
            return;
        if (typeof value.subscriptionId !== "string")
            return;
        const replayKey = `${value.type.startsWith("stage.") ? value.sessionId ?? "stage" : "kernel"}:${value.subscriptionId}`;
        if (isSubscriptionRequest(value.type)) {
            this.#replayMessages.set(replayKey, message);
        }
        else if (isUnsubscribeRequest(value.type)) {
            this.#replayMessages.delete(replayKey);
        }
    }
}
function parseRecord(message) {
    try {
        const value = JSON.parse(message);
        return isRecord(value) ? value : undefined;
    }
    catch {
        return undefined;
    }
}
function responseSessionId(value) {
    if (value.ok !== true || !isRecord(value.value) || typeof value.value.sessionId !== "string")
        return undefined;
    return value.value.sessionId;
}
function isSubscriptionRequest(type) {
    return type === "live-query.subscribe" || type === "event.subscribe" || type === "stage.live-query.subscribe" || type === "stage.event.subscribe";
}
function isUnsubscribeRequest(type) {
    return type === "live-query.unsubscribe" || type === "event.unsubscribe" || type === "stage.live-query.unsubscribe" || type === "stage.event.unsubscribe";
}
function isControlMessage(value) {
    if (!isRecord(value) || typeof value.type !== "string")
        return false;
    switch (value.type) {
        case "leader.announce":
        case "leader.closed":
            return isNonEmptyString(value.leaderId);
        case "leader.whois":
        case "sync.wake":
            return true;
        case "follower.heartbeat":
            return isNonEmptyString(value.tabId);
        case "rpc.message":
            return isNonEmptyString(value.connectionId) && isNonEmptyString(value.fromTabId) && typeof value.message === "string";
        case "rpc.close":
            return isNonEmptyString(value.connectionId) && isNonEmptyString(value.fromTabId);
        case "rpc.expired":
            return isNonEmptyString(value.connectionId) && isNonEmptyString(value.toTabId);
        case "rpc.reply":
            return isNonEmptyString(value.connectionId) && typeof value.message === "string" && isNonEmptyString(value.toTabId);
        default:
            return false;
    }
}
function defaultMaintenanceScheduler(delayMs, callback) {
    const handle = globalThis.setTimeout(callback, delayMs);
    return { cancel: () => globalThis.clearTimeout(handle) };
}
function isNonEmptyString(value) {
    return typeof value === "string" && value.length > 0;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=leader-election.js.map